import React from "react";

/**
 * Unified SVG circle background. Use `variant` to switch between the two
 * original assets without duplicating component code across files.
 */
const SetupCircleBackground = React.forwardRef<
  SVGSVGElement,
  React.SVGProps<SVGSVGElement> & { variant: "first" | "second" }
>(({ variant, className, ...props }, ref) => {
  if (variant === "first") {
    // From SetupFirstCircleBackground
    return (
      <svg
        {...props}
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        width={2388}
        height={1849}
        fill="none"
        className={className}
      >
        <g filter="url(#a_first)">
          <ellipse
            cx={1117.95}
            cy={605.563}
            fill="#B9E7E9"
            rx={869.948}
            ry={842.934}
          />
        </g>
        <defs>
          <filter
            id="a_first"
            width={2539.9}
            height={2485.87}
            x={-152}
            y={-637.371}
            colorInterpolationFilters="sRGB"
            filterUnits="userSpaceOnUse"
          >
            <feFlood floodOpacity={0} result="BackgroundImageFix" />
            <feBlend
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              result="effect1_foregroundBlur_1447_58239"
              stdDeviation={200}
            />
          </filter>
        </defs>
      </svg>
    );
  }
  // From SetupSecondCircleBackground
  return (
    <svg
      {...props}
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      width={2408}
      height={2014}
      fill="none"
      className={className}
    >
      <g filter="url(#a_second)">
        <ellipse
          cx={1655.19}
          cy={605.5}
          fill="#C2C9FF"
          rx={1054.24}
          ry={1021.5}
        />
      </g>
      <defs>
        <filter
          id="a_second"
          width={3308.47}
          height={3243}
          x={0.954}
          y={-1016}
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity={0} result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur
            result="effect1_foregroundBlur_1447_58240"
            stdDeviation={300}
          />
        </filter>
      </defs>
    </svg>
  );
});
SetupCircleBackground.displayName = "SetupCircleBackground";

function AnimatedCircle({
  variant,
  style,
  floatX = [0],
  floatY = [0],
  rotate = [0],
  hueRotate = [0],
  duration = 0,
  opacity,
  blur
}: {
  variant: "first" | "second";
  style: React.CSSProperties;
  floatX?: number[];
  floatY?: number[];
  rotate?: number[];
  hueRotate?: number[];
  duration?: number;
  opacity: string;
  blur?: string;
}) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute select-none"
      style={style}
    >
      <SetupCircleBackground
        variant={variant}
        className={`${opacity} ${blur || ""}`}
      />
    </div>
  );
}

// Helper to position using viewport units so placement responds to window size
const pos = (coords: {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
}): React.CSSProperties => {
  const style: React.CSSProperties = {};
  if (coords.left !== undefined) style.left = `${coords.left}vw`;
  if (coords.right !== undefined) style.right = `${coords.right}vw`;
  if (coords.top !== undefined) style.top = `${coords.top}vh`;
  if (coords.bottom !== undefined) style.bottom = `${coords.bottom}vh`;
  return style;
};

export function SetupAnimatedBackground({
  children,
  className = "",
  isOpen = true
}: {
  children?: React.ReactNode;
  className?: string;
  isOpen?: boolean;
}) {
  return (
    <div
      data-state={isOpen ? "open" : "closed"}
      className={`-z-10 fixed inset-0 overflow-hidden bg-[#F7F9FB] ${className}`}
    >
      <div className="absolute inset-0">
        {/* Foreground soft blob (bottom/left) */}
        <div>
          {/* Back-most glow (left/top) */}
          <AnimatedCircle
            variant="first"
            style={pos({ left: -33, top: -25 })}
            duration={10}
            opacity="opacity-60"
            blur="blur-2xl"
          />

          {/* Middle glow (right/top) */}
          <AnimatedCircle
            variant="second"
            style={pos({ right: -33, top: -10 })}
            duration={12}
            opacity="opacity-70"
            blur="blur-3xl"
          />
          <div className="relative" style={{ filter: "blur(64px)" }}>
            <SetupCircleBackground variant="first" className="opacity-50" />
            <div
              className="absolute inset-0 mix-blend-soft-light"
              style={{
                background:
                  "radial-gradient(60% 60% at 50% 50%, rgba(109,166,255,0.25) 0%, rgba(194,201,255,0.15) 50%, rgba(185,231,233,0.1) 100%)"
              }}
            />
          </div>
        </div>

        {/* Gentle vignette */}
        <div
          style={{
            maskImage:
              "radial-gradient(70% 60% at 50% 40%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.9) 50%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0) 100%)",
            WebkitMaskImage:
              "radial-gradient(70% 60% at 50% 40%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.9) 50%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0) 100%)",
            background:
              "radial-gradient(60% 50% at 50% 40%, rgba(255,255,255,0) 0%, rgba(0,0,0,0.12) 100%)"
          }}
        />
      </div>

      {children ? (
        <div className="relative z-10 h-full w-full">{children}</div>
      ) : null}
    </div>
  );
}
